import React, { useContext, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { FaEdit, FaTrash, FaPlus, FaFilePdf, FaFileExcel, FaTimes, FaMinus, FaInfoCircle, FaCog, FaSearch, FaArrowDown, FaArrowUp, FaBoxOpen, FaTag } from 'react-icons/fa';
import PageLayout from './PageLayout';
import ExemploBadge from './ExemploBadge';
import { theme, t } from '../theme';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ProductForm from './ProductForm';
import StockEntry from './StockEntry';
import StockExit from './StockExit';
import { norm } from '../utils';
import { useConfirm } from './ConfirmProvider';
import useViewMode from '../hooks/useViewMode';
import ViewToggle from './ViewToggle';
import { entregarArquivo, entregarPdf, MIME_XLSX } from '../platform/arquivos';
import { AuthContext } from '../context/AuthContext';
import { moduloLigado, MODULO_ETIQUETAS } from '../config/modulos';
import EtiquetaModal from './EtiquetaModal';

const tableActionBtn = (color, bg) => ({
  width: 32, height: 32, borderRadius: 8, border: 'none',
  background: bg, color, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
});

const thTabela = {
    fontSize: 11, textTransform: 'uppercase', color: '#888',
    fontWeight: 600, letterSpacing: '0.04em',
};

const InventoryList = () => {
    const { tenant } = useContext(AuthContext);
    const temEtiquetas = moduloLigado(tenant, MODULO_ETIQUETAS);
    const [produtos, setProdutos] = useState([]);
    const [produtosFiltrados, setProdutosFiltrados] = useState([]);
    const [produtoEditado, setProdutoEditado] = useState(null);
    const [mostrarModalEdicao, setMostrarModalEdicao] = useState(false);
    const [lotes, setLotes] = useState({});
    const [filtroProduto, setFiltroProduto] = useState('');
    const [estoqueCritico, setEstoqueCritico] = useState(0);
    const confirm = useConfirm();
    const { mode: gridMode, setMode: setGridMode, effective: gridEffective } = useViewMode('view:inventario')
    // Etiqueta da embalagem aberta: nasce do produto + do lote mais antigo com saldo.
    const [etiquetaDe, setEtiquetaDe] = useState(null);
    const [exibirAlerta, setExibirAlerta] = useState(true);
    const [produtoMovimentacao, setProdutoMovimentacao] = useState(null);  
    const [tipoMovimentacao, setTipoMovimentacao] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // Atalho "Entrada/Saída" (/estoque?movimentar=1) abre a movimentacao SEM
    // produto pre-selecionado — e o unico caminho em que o campo de codigo de
    // barras aparece, e portanto onde o leitor da camera fica acessivel.
    // Aceita ?movimentar=saida para cair direto na saida.
    useEffect(() => {
        const mov = searchParams.get('movimentar');
        if (!mov) return;
        setProdutoMovimentacao(null);
        setTipoMovimentacao(mov === 'saida' ? 'saida' : 'entrada');
        // Limpa da URL para o modal nao reabrir ao voltar para a tela.
        searchParams.delete('movimentar');
        setSearchParams(searchParams, { replace: true });
    }, [searchParams, setSearchParams]);
    const [filtrarCriticos, setFiltrarCriticos] = useState(false);
    const [filtroTipo, setFiltroTipo] = useState('all'); // all | true | false
    const [viewMode, setViewMode] = useState('table');
    const [isMobile, setIsMobile] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [receberEmail, setReceberEmail] = useState(true);

    // Detectar mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth <= 768) {
                setViewMode('cards');
            } else {
                setViewMode('table');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        carregarProdutos();
        carregarLotes();
        carregarPreferenciaNotificacao();
    }, []);

    const queroAbrirNovoRef = React.useRef(
        typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('novo') === '1'
    );
    useEffect(() => {
        if (!queroAbrirNovoRef.current) return;
        queroAbrirNovoRef.current = false;
        setProdutoEditado(null);
        setMostrarModalEdicao(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const carregarPreferenciaNotificacao = async () => {
        try {
            const res = await api.get('/notificacoes/preferencia');
            setReceberEmail(res.data.receber_email);
        } catch {}
    };

    const toggleReceberEmail = async () => {
        const novo = !receberEmail;
        setReceberEmail(novo);
        try {
            await api.put('/notificacoes/preferencia', { receber_email: novo });
        } catch {
            setReceberEmail(!novo);
        }
    };

    useEffect(() => {
        let produtosFiltrados = produtos.filter(produto =>
            norm(produto.nome).includes(norm(filtroProduto))
        );

        if (filtroTipo === 'true') {
            produtosFiltrados = produtosFiltrados.filter(p => p.controla_estoque !== false);
        } else if (filtroTipo === 'false') {
            produtosFiltrados = produtosFiltrados.filter(p => p.controla_estoque === false);
        }

        if (filtrarCriticos) {
            produtosFiltrados = produtosFiltrados.filter(produto => {
                if (produto.controla_estoque === false) return false;
                const estoqueAtual = Number(produto.estoque_atual);
                const estoqueMinimo = Number(produto.estoque_minimo);
                return estoqueAtual <= estoqueMinimo && !(estoqueMinimo === 1 && estoqueAtual === 1);
            });
        }

        setProdutosFiltrados(produtosFiltrados);
    }, [filtroProduto, produtos, filtrarCriticos, filtroTipo]);

    const carregarProdutos = async (tentativa = 0) => {
        try {
            const response = await api.get('/produtos');
            const produtosOrdenados = response.data.sort((a, b) => a.nome.localeCompare(b.nome));
            setProdutos(produtosOrdenados);

            const produtosCriticos = response.data.filter(produto => {
                const estoqueAtual = Number(produto.estoque_atual);
                const estoqueMinimo = Number(produto.estoque_minimo);
                return estoqueAtual <= estoqueMinimo && !(estoqueMinimo === 1 && estoqueAtual === 1);
            });

            setEstoqueCritico(produtosCriticos.length);
            setExibirAlerta(produtosCriticos.length > 0);
        } catch (error) {
            console.error("Erro ao buscar produtos:", error);
            if (tentativa < 2) setTimeout(() => carregarProdutos(tentativa + 1), 1000);
        }
    };

    const carregarLotes = async () => {
        try {
            const response = await api.get('/lotes');
            const lotesPorProduto = {};
        
            response.data.forEach(lote => {
                if (!lotesPorProduto[lote.produto_id] || lote.id > lotesPorProduto[lote.produto_id].id) {
                    lotesPorProduto[lote.produto_id] = {
                        ...lote,
                        preco_custo: Number(lote.preco_custo) || 0
                    };
                }
            });
    
            setLotes(lotesPorProduto);
        } catch (error) {
            console.error("Erro ao buscar lotes:", error);
        }
    };

    const obterDataHoraAtual = () => {
        return new Date().toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).replace(/[/: ]/g, '_');
    };

    const exportarParaExcel = () => {
        const dataHora = obterDataHoraAtual();
        const worksheet = XLSX.utils.json_to_sheet(produtos.map(produto => {
            const lote = lotes[produto.id] || {};
            return {
                Nome: produto.nome,
                "Código de Barras": produto.codigo_barras,
                "Preço Custo": lote.preco_custo ? `R$ ${Number(lote.preco_custo).toFixed(2)}` : "-",
                Peso: produto.peso_embalagem,
                Unidade: produto.unidade_peso,
                "Estoque Mínimo": produto.estoque_minimo,
                "Estoque Atual": produto.estoque_atual
            };
        }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inventário");
        // writeFile baixa por <a download>, que o webview ignora — no app o
        // arquivo vai para a folha de compartilhamento.
        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        entregarArquivo(new Blob([buffer], { type: MIME_XLSX }), `Inventario_${dataHora}.xlsx`);
    };

    const exportarParaPDF = () => {
        const doc = new jsPDF();
        const dataHora = obterDataHoraAtual();

        doc.text("Relatório de Inventário", 14, 10);
        doc.text(`Exportado em: ${dataHora.replace(/_/g, ':')}`, 14, 18);

        autoTable(doc, {
            head: [["Nome", "Código de Barras", "Preço Custo", "Peso", "Unidade", "Estoque Mínimo", "Estoque Atual"]],
            body: produtos.map(produto => {
                const lote = lotes[produto.id] || {};
                return [
                    produto.nome,
                    produto.codigo_barras,
                    lote.preco_custo ? `R$ ${Number(lote.preco_custo).toFixed(2)}` : "-",
                    produto.peso_embalagem,
                    produto.unidade_peso,
                    produto.estoque_minimo,
                    produto.estoque_atual
                ];
            }),
            startY: 25,
        });

        entregarPdf(doc, `Inventario_${dataHora}.pdf`);
    };

    /**
     * Etiqueta de embalagem aberta: gera a etiqueta a partir do insumo do estoque.
     *
     * A validade do FABRICANTE vem do lote mais antigo com saldo (que e o que
     * a cozinha abre primeiro, e o que o FIFO vai consumir). A validade que
     * passa a valer e a de depois de aberto — e o par das duas datas e o
     * motivo de a etiqueta existir para insumo.
     */
    const abrirPote = async (produto) => {
        try {
            const r = await api.get('/api/etiquetas/sugestao', { params: { produto_id: produto.id } });
            setEtiquetaDe({
                origem: 'produto',
                produto_id: r.data.produto_id,
                lote_id: r.data.lote_id,
                nome: r.data.nome,
                conservacao: r.data.conservacao,
                quantidade: r.data.quantidade != null ? String(r.data.quantidade) : '',
                unidade_medida: r.data.unidade_medida || 'g',
                lote_texto: r.data.lote_texto || '',
                validade_original: r.data.validade_original || null,
                lotes: r.data.lotes || [],
                modo_validade: r.data.modo_validade || 'dias',
                prazo_dias: String(r.data.prazo_dias),
            });
        } catch {
            // Sem sugestao a etiqueta ainda pode sair: melhor abrir o modal com
            // o nome preenchido do que travar por causa de um lote ausente.
            setEtiquetaDe({ origem: 'produto', produto_id: produto.id, nome: produto.nome,
                            conservacao: 'refrigerado', unidade_medida: produto.unidade_peso || 'g' });
        }
    };

    const handleExcluirProduto = async (id) => {
        if (await confirm("Tem certeza que deseja excluir este produto?", { danger: true, confirmText: 'Excluir' })) {
            const backup = [...produtos];
            setProdutos(prev => prev.filter(p => p.id !== id));
            try {
                await api.delete(`/produtos/${id}`);
                toast.success('Produto excluído com sucesso!');
            } catch (error) {
                setProdutos(backup);
                if (error.response?.data?.fichas) {
                    toast.error(
                        <div>
                            <strong>{error.response.data.mensagem}</strong>
                            <div style={{ marginTop: '10px', fontSize: '14px' }}>
                                {error.response.data.fichas.map((ficha, idx) => (
                                    <div key={idx}>{ficha}</div>
                                ))}
                            </div>
                        </div>,
                        {
                            autoClose: 8000,
                            style: { width: '400px' }
                        }
                    );
                } else {
                    toast.error(error.response?.data?.erro || 'Erro ao excluir produto');
                }
            }
        }
    };

    const handleEditarProduto = (produto) => {
        setProdutoEditado(produto);
        setMostrarModalEdicao(true);
    };

    const fecharModalEdicao = () => {
        setProdutoEditado(null);
        setMostrarModalEdicao(false);
        carregarProdutos();
        carregarLotes();
    };

    // Componente Card para Mobile
    const ProductCard = ({ produto }) => {
        const lote = lotes[produto.id] || {};
        const isStockCritical = produto.estoque_atual <= 0;
        const isStockLow = produto.estoque_atual > 0 && produto.estoque_atual <= produto.estoque_minimo;

        return (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 text-base truncate">{produto.nome}<ExemploBadge origem={produto.origem} /></h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-gray-500">{produto.codigo_barras}</span>
                            {(() => { const c = lote?.preco_custo ?? produto.preco_custo ?? produto.custo_medio ?? produto.custo_fixo; return c ? (<span className="text-xs font-medium text-green-600">R$ {Number(c).toFixed(2)}</span>) : null; })()}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div>
                        <span className="text-gray-500">Peso:</span>
                        <span className="ml-1 text-gray-700">{produto.peso_embalagem} {produto.unidade_peso}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Est. Mín:</span>
                        <span className="ml-1 text-gray-700">{produto.controla_estoque === false ? '-' : produto.estoque_minimo}</span>
                    </div>
                    <div className="col-span-2">
                        <span className="text-gray-500">Estoque Atual:</span>
                        {produto.controla_estoque === false ? (
                            <span className="ml-1 font-semibold text-gray-500">-</span>
                        ) : (
                            <>
                                <span className={`ml-1 font-semibold ${
                                    isStockCritical ? 'text-red-600' :
                                    isStockLow ? 'text-yellow-600' :
                                    'text-green-600'
                                }`}>
                                    {produto.estoque_atual}
                                </span>
                                {isStockCritical && <span className="ml-2 text-red-500 text-xs">Esgotado</span>}
                                {isStockLow && <span className="ml-2 text-yellow-500 text-xs">Baixo</span>}
                            </>
                        )}
                    </div>
                </div>

                <div className="flex gap-1">
                    {produto.controla_estoque !== false && (<>
                    <button
                        className="flex-1 bg-green-500 text-white p-2 rounded text-xs hover:bg-green-600"
                        onClick={() => {
                            setProdutoMovimentacao(produto);
                            setTipoMovimentacao('entrada');
                        }}
                    >
                        <FaPlus className="inline" /> Entrada
                    </button>
                    <button
                        className="flex-1 bg-yellow-500 text-white p-2 rounded text-xs hover:bg-yellow-600"
                        onClick={() => {
                            setProdutoMovimentacao(produto);
                            setTipoMovimentacao('saida');
                        }}
                    >
                        <FaMinus className="inline" /> Saída
                    </button>
                    </>)}
                    <button
                        className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                        onClick={() => handleEditarProduto(produto)}
                    >
                        <FaEdit size={14} />
                    </button>
                    <button 
                        className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                        onClick={() => handleExcluirProduto(produto.id)}
                    >
                        <FaTrash size={14} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <PageLayout title="Produtos"
            action={
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={exportarParaExcel} style={t.btnSecondary}><FaFileExcel /> Excel</button>
                    <button onClick={exportarParaPDF} style={t.btnSecondary}><FaFilePdf /> PDF</button>
                    <button onClick={() => { setProdutoEditado(null); setMostrarModalEdicao(true); }} style={t.btnPrimary}><FaPlus /> Novo Produto</button>
                    <div style={{ position: 'relative' }}>
                        <button onClick={() => setShowSettings(!showSettings)} style={{ ...t.btnIcon, color: showSettings ? theme.colors.primary : theme.colors.textMuted }} title="Configurações"><FaCog /></button>
                        {showSettings && (
                            <div style={{ position: 'absolute', right: 0, top: 40, background: '#fff', borderRadius: 12, boxShadow: theme.shadow.lg, padding: '14px 18px', minWidth: 260, zIndex: 100, border: `1px solid ${theme.colors.border}` }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: theme.colors.text }}>
                                    <input type="checkbox" checked={receberEmail} onChange={toggleReceberEmail} style={{ width: 18, height: 18, accentColor: theme.colors.primary }} />
                                    Receber avisos de estoque baixo por e-mail
                                </label>
                            </div>
                        )}
                    </div>
                </div>
            }>

            {/* Toast de Estoque Crítico */}
            {estoqueCritico > 0 && exibirAlerta && (
                <div style={{ position: 'fixed', bottom: 24, right: 24, maxWidth: 340, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', border: `1px solid ${theme.colors.border}`, borderRadius: 16, padding: 16, boxShadow: theme.shadow.lg, zIndex: 50 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: theme.colors.warning, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FaInfoCircle style={{ color: '#fff' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <strong style={{ fontSize: 14, color: theme.colors.text }}>Estoque Baixo</strong>
                                <button onClick={() => setExibirAlerta(false)} style={{ ...t.btnIcon, width: 24, height: 24 }}><FaTimes style={{ fontSize: 12 }} /></button>
                            </div>
                            <p style={{ fontSize: 13, color: theme.colors.textSecondary, margin: '0 0 10px' }}>{estoqueCritico} {estoqueCritico === 1 ? 'produto precisa' : 'produtos precisam'} de atenção</p>
                            <button onClick={() => { setFiltrarCriticos(true); setExibirAlerta(false); }} style={{ ...t.btnSmall, background: theme.colors.warning }}>Filtrar produtos →</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Busca + Filtros */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                    <FaSearch style={{ position: 'absolute', left: 14, top: 13, color: theme.colors.textMuted }} />
                    <input placeholder="Buscar produto..." value={filtroProduto} onChange={(e) => setFiltroProduto(e.target.value)} style={{ ...t.input, paddingLeft: 40 }} />
                </div>
                <button onClick={() => setFiltrarCriticos(!filtrarCriticos)}
                    style={{ ...t.btnSecondary, ...(filtrarCriticos ? { background: theme.colors.danger, color: '#fff', borderColor: theme.colors.danger } : {}) }}>
                    <FaInfoCircle /> {filtrarCriticos ? `Críticos (${produtosFiltrados.length})` : 'Críticos'}
                </button>
                <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} style={{ ...t.select, maxWidth: 200 }}>
                    <option value="all">Todos</option>
                    <option value="true">📦 Com controle</option>
                    <option value="false">🏷️ Sem controle</option>
                </select>
                <ViewToggle mode={gridMode} setMode={setGridMode} />
            </div>

            {/* Tabela */}
            {produtosFiltrados.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: theme.colors.textMuted }}>
                    <FaBoxOpen size={40} style={{ marginBottom: 12, color: theme.colors.border }} />
                    <p>{filtrarCriticos ? 'Nenhum produto com estoque crítico' : filtroProduto ? 'Nenhum produto encontrado' : 'Nenhum produto cadastrado'}</p>
                </div>
            ) : gridEffective === 'tabela' ? (
                <div className="table-responsive" style={{ background: '#fff', borderRadius: 12, padding: 8, border: '1px solid #f0f0f0', overflowX: 'auto' }}>
                    <table className="table table-sm align-middle" style={{ marginBottom: 0, width: '100%' }}>
                        <thead style={{ background: '#faf7f5' }}>
                            <tr>
                                {/* `width: 1%` encolhe a coluna ate o conteudo: o navegador da o
                                    resto para o Nome, que e o que a pessoa procura. Sem isso a
                                    largura era dividida em partes iguais e o nome era o primeiro
                                    a ser espremido. */}
                                <th style={thTabela}>Nome</th>
                                <th style={{ ...thTabela, width: '1%', whiteSpace: 'nowrap' }}>Tipo</th>
                                <th className="text-end" style={{ ...thTabela, width: '1%', whiteSpace: 'nowrap' }}>Custo</th>
                                <th className="text-end" style={{ ...thTabela, width: '1%', whiteSpace: 'nowrap' }}>Peso</th>
                                <th className="text-end" style={{ ...thTabela, width: '1%', whiteSpace: 'nowrap' }}>Mínimo</th>
                                <th className="text-end" style={{ ...thTabela, width: '1%', whiteSpace: 'nowrap' }}>Estoque</th>
                                <th className="text-center" style={{ ...thTabela, width: '1%', whiteSpace: 'nowrap' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {produtosFiltrados.map((produto) => {
                                const lote = lotes[produto.id] || {};
                                const semControle = produto.controla_estoque === false;
                                const isStockCritical = !semControle && produto.estoque_atual <= 0;
                                const isStockLow = !semControle && produto.estoque_atual > 0 && produto.estoque_atual <= produto.estoque_minimo;
                                const stockBg = semControle ? '#f1f3f5' : isStockCritical ? '#fdecea' : isStockLow ? '#fff7e6' : '#e8f5e9';
                                const stockColor = semControle ? '#888' : isStockCritical ? '#e74c3c' : isStockLow ? '#d97706' : '#2e7d32';
                                const custoVal = lote?.preco_custo ?? produto.preco_custo ?? produto.custo_medio ?? produto.custo_fixo;
                                return (
                                    <tr key={produto.id}>
                                        <td><strong>{produto.nome}</strong></td>
                                        {/* `nowrap`: sem ele "Com controle" quebrava depois do
                                            "Com" e esticava a altura de TODA a linha. */}
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <span style={{ background: semControle ? '#f1f3f5' : '#e8f3ff', color: semControle ? '#666' : '#1976d2', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-block' }}>
                                                {semControle ? '🏷️ Sem controle' : '📦 Com controle'}
                                            </span>
                                        </td>
                                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{custoVal ? `R$ ${Number(custoVal).toFixed(2)}` : '-'}</td>
                                        {/* Sem peso, so o traco: antes saia "- kg", como se a
                                            unidade fosse conhecida e o numero nao. */}
                                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                            {produto.peso_embalagem != null
                                                ? `${produto.peso_embalagem} ${produto.unidade_peso || ''}`.trim()
                                                : '-'}
                                        </td>
                                        <td className="text-end">{semControle ? '-' : produto.estoque_minimo}</td>
                                        <td className="text-end">
                                            <span style={{ background: stockBg, color: stockColor, padding: '2px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                                                {semControle ? '-' : produto.estoque_atual}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <div style={{ display: 'inline-flex', gap: 4, whiteSpace: 'nowrap' }}>
                                                {!semControle && (
                                                    <>
                                                        <button onClick={() => { setProdutoMovimentacao(produto); setTipoMovimentacao('entrada'); }} title="Entrada" style={tableActionBtn(theme.colors.success, '#e8f5e9')}><FaPlus size={14} /></button>
                                                        <button onClick={() => { setProdutoMovimentacao(produto); setTipoMovimentacao('saida'); }} title="Saída" style={tableActionBtn(theme.colors.danger, '#fdecea')}><FaMinus size={14} /></button>
                                                    </>
                                                )}
                                                {/* Vale para os dois: quem controla estoque tambem abre embalagem, e
                                                    a etiqueta dele carrega o lote e a validade do fabricante. */}
                                                {temEtiquetas && (
                                                    <button onClick={() => abrirPote(produto)} title="Imprimir etiqueta"
                                                        style={tableActionBtn('#1e8449', '#eafaf1')}><FaTag size={14} /></button>
                                                )}
                                                <button onClick={() => handleEditarProduto(produto)} title="Editar" style={tableActionBtn(theme.colors.info, '#f1f3f5')}><FaEdit size={14} /></button>
                                                <button onClick={() => handleExcluirProduto(produto.id)} title="Excluir" style={tableActionBtn(theme.colors.danger, '#fdecea')}><FaTrash size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                    {produtosFiltrados.map((produto) => {
                        const lote = lotes[produto.id] || {};
                        const semControle = produto.controla_estoque === false;
                        const isStockCritical = !semControle && produto.estoque_atual <= 0;
                        const isStockLow = !semControle && produto.estoque_atual > 0 && produto.estoque_atual <= produto.estoque_minimo;
                        const stripeColor = semControle ? theme.colors.textMuted : isStockCritical ? theme.colors.danger : isStockLow ? theme.colors.warning : theme.colors.success;
                        const stockColor = semControle ? theme.colors.textSecondary : isStockCritical ? theme.colors.danger : isStockLow ? theme.colors.warning : theme.colors.success;
                        const custoVal = lote?.preco_custo ?? produto.preco_custo ?? produto.custo_medio ?? produto.custo_fixo;
                        return (
                            <div key={produto.id} style={{ background: '#fff', borderRadius: 14, border: `1px solid ${theme.colors.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ height: 4, background: stripeColor }} />
                                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ fontWeight: 600, color: theme.colors.text, fontSize: 15, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <span title={semControle ? 'Apenas precificação' : 'Controla estoque'} style={{ marginRight: 6 }}>{semControle ? '🏷️' : '📦'}</span>
                                                {produto.nome}
                                            </div>
                                            <div style={{ marginTop: 6 }}>
                                                <span style={t.badge(theme.colors.info, theme.colors.infoLight)}>{produto.unidade_peso}</span>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.colors.textMuted, fontWeight: 600 }}>Custo</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>{custoVal ? `R$ ${Number(custoVal).toFixed(2)}` : '-'}</div>
                                        </div>
                                    </div>
                                    {/* Numeros e acoes na MESMA linha. Antes eram tres faixas empilhadas —
                                        grade 3x2 com rotulo em cima do valor, divisoria, botoes — o que dobrava
                                        a altura do card para caber tres numeros curtos. */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2 }}>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 12px', fontSize: 12, minWidth: 0 }}>
                                        {[['Peso', `${produto.peso_embalagem ?? '-'} ${produto.unidade_peso || ''}`.trim(), theme.colors.textSecondary],
                                          ...(semControle ? [] : [['Mínimo', String(produto.estoque_minimo ?? '-'), theme.colors.textSecondary],
                                                                 ['Estoque', String(produto.estoque_atual ?? '-'), stockColor]])].map(([rot, val, cor]) => (
                                          <span key={rot} style={{ whiteSpace: 'nowrap' }}>
                                            <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.colors.textMuted, fontWeight: 600, marginRight: 4 }}>{rot}</span>
                                            <span style={{ color: cor, fontWeight: 700 }}>{val}</span>
                                          </span>
                                        ))}
                                      </div>
                                      <div style={{ display: 'flex', gap: 6, flexShrink: 0, justifyContent: 'flex-end' }}>
                                        {/* Etiqueta so para quem NAO controla estoque: nos que controlam,
                                            o momento de etiquetar e a SAIDA ("usei 4, sobrou 1 aberto"),
                                            e um botao a mais aqui so espremia o card. */}
                                        {temEtiquetas && (
                                            <button onClick={() => abrirPote(produto)} title="Imprimir etiqueta"
                                                style={{ width: 34, height: 34, borderRadius: 10, border: 'none', background: '#eafaf1', color: '#1e8449', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaTag size={13} /></button>
                                        )}
                                        {!semControle && (
                                            <>
                                                <button onClick={() => { setProdutoMovimentacao(produto); setTipoMovimentacao('entrada'); }} title="Entrada"
                                                    style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#e8f5e9', color: theme.colors.success, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaPlus size={16} /></button>
                                                <button onClick={() => { setProdutoMovimentacao(produto); setTipoMovimentacao('saida'); }} title="Saída"
                                                    style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#fdecea', color: theme.colors.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaMinus size={16} /></button>
                                            </>
                                        )}
                                                                                <button onClick={() => handleEditarProduto(produto)} title="Editar"
                                            style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#f1f3f5', color: theme.colors.info, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaEdit size={16} /></button>
                                        <button onClick={() => handleExcluirProduto(produto.id)} title="Excluir"
                                            style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#fdecea', color: theme.colors.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaTrash size={16} /></button>
                                      </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modais */}
            {mostrarModalEdicao && <ProductForm produtoEditado={produtoEditado} fecharModal={fecharModalEdicao} />}
            {tipoMovimentacao === 'entrada' && (
                <StockEntry produtoSelecionado={produtoMovimentacao} isModal={true}
                    fecharModal={async () => { setProdutoMovimentacao(null); setTipoMovimentacao(null); await carregarProdutos(); await carregarLotes(); }} />
            )}
            {tipoMovimentacao === 'saida' && (
                <StockExit produtoSelecionado={produtoMovimentacao} isModal={true}
                    fecharModal={async () => { setProdutoMovimentacao(null); setTipoMovimentacao(null); await carregarProdutos(); await carregarLotes(); }} />
            )}
            {etiquetaDe && (
                <EtiquetaModal inicial={etiquetaDe} onFechar={() => setEtiquetaDe(null)} />
            )}
        </PageLayout>
    );
};

export default InventoryList;